import { useMutation, useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Waiting } from "../../../../animations";
import { ErrorFallback, Head } from "../../../../components/core";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../contexts/NotifyContext";
import {
  EDIT_PRICINGLEVEL,
  GET_PRICINGLEVEL_BY_ID,
  GET_PRICINGLEVELS,
} from "../../../../graphql/inventory/pricing/pricing-levels";
import { PreviewProduct } from "../../../../graphql/inventory/products";
import Form from "./components/Form";

type PricingMethodField = {
  fieldType: string;
  fieldKey: string;
  fieldName: string;
  fieldOptions: string[];
};

type PricingMethod = {
  id: string;
  name: string;
  sellPriceFormula: string[];
  pricingMethodFields: PricingMethodField[];
  createdAt: string;
  status: boolean;
};

type PricingLevel = {
  id: string;
  name: string;
  pricingMethod: PricingMethod;
  productType: number;
  products: PreviewProduct[];
  createdAt: string;
  status: boolean;
};

const PricingLevelUpdate = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { addNotify } = useNotifyContext();
  let { pricingLevelId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_PRICINGLEVEL_BY_ID, {
    variables: {
      id: pricingLevelId,
    },
  });
  const pricingLevel: PricingLevel = useMemo(
    () => data?.fetchPricingLevel,
    [data]
  );

  const [updatePricingLevel] = useMutation(EDIT_PRICINGLEVEL, {
    refetchQueries: [
      {
        query: GET_PRICINGLEVELS,
      },
    ],
    awaitRefetchQueries: true,
  });

  const handleSubmit = (
    values: any,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    updatePricingLevel({
      variables: {
        id: pricingLevel?.id,
        name: values.name,
        pricingMethodId: values.pricingMethod
          ? parseInt(values.pricingMethod.value)
          : null,
        productType: values.productType,
        productIds: values.productIds,
        status: values.status,
      },
    })
      .then(({ data }) => {
        actions.setSubmitting(false);
        if (data?.pricingLevelUpdate) {
          addNotify({
            type: NotifyType.SUCCESS,
            title: "Pricing level updated successfully",
          });
          return navigate(`/inventory/pricing/pricing-levels`);
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Pricing level update failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Pricing level update failed",
          message: error.message,
        });
      });
  };

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={PricingLevelUpdateResource.name}
        heading="Pricing Levels"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Pricing",
            href: "/inventory/pricing",
          },
          {
            name: "Pricing Levels",
            href: "/inventory/pricing/pricing-levels",
          },
          {
            name: PricingLevelUpdateResource.name,
            href: null,
          },
        ]}
      />
      <div className="mx-auto max-w-3xl py-6 sm:py-8">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_update_pricing_level")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_update_pricing_level")}
              </p>
            </div>
          </div>

          {loading || !pricingLevel ? (
            <Waiting />
          ) : (
            <Form
              initialValues={{
                name: pricingLevel.name ?? "",
                pricingMethod: pricingLevel.pricingMethod
                  ? {
                      label: pricingLevel.pricingMethod.name,
                      value: pricingLevel.pricingMethod.id,
                    }
                  : null,
                productType: pricingLevel.productType ?? 0,
                productIds: pricingLevel.products
                  ? pricingLevel.products.map((p) => parseInt(p.id))
                  : [],
                status: pricingLevel.status ? true : false,
              }}
              products={pricingLevel.products}
              onSubmit={handleSubmit}
              actionLabel={t("text_update")}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PricingLevelUpdate;
export const PricingLevelUpdateResource: ResourceProps = {
  name: "Edit Pricing Level",
  description: "Update an existing pricing level",
  access: ["update-pricings"],
  path: "pricing-levels/:pricingLevelId",
};
